import { el, clear, onTeardown } from '../lib/dom.js';
import { loadSnapshot } from '../data/snapshot.js';
import { settings } from '../store/settings.js';
import { shinyHunts, addHunt, updateHunt, bumpHunt, removeHunt } from '../store/shinyHunts.js';
import { toggle, entry } from '../store/selection.js';
import { buzz } from '../lib/haptics.js';
import { Sprite } from '../components/Sprite.js';
import { openPokemonPicker } from '../components/PokemonPicker.js';
import { chooseDialog } from '../lib/dialog.js';

// Representative full-game odds (as 1/N), with a shiny-charm variant.
export const METHODS = [
  { id: 'full', label: 'Full odds', base: 4096, charm: 1365 },
  { id: 'masuda', label: 'Masuda Method', base: 683, charm: 512 },
  { id: 'sos', label: 'SOS chaining (31+)', base: 1365, charm: 1024 },
  { id: 'radar', label: 'Poké Radar (chain 40)', base: 99, charm: 99 },
  { id: 'dexnav', label: 'DexNav (high search level)', base: 1365, charm: 1024 },
  { id: 'combo', label: 'Catch Combo (31+)', base: 1024, charm: 819 },
  { id: 'dmax', label: 'Dynamax Adventure', base: 300, charm: 100 },
  { id: 'outbreak', label: 'Mass Outbreak (SV, 60+)', base: 512, charm: 205 },
  { id: 'massive', label: 'Massive Outbreak (Legends)', base: 158, charm: 128 },
];
const methodById = (id) => METHODS.find((m) => m.id === id) || METHODS[0];

function oddsFor(h) {
  if (h.customOdds) return Math.max(1, h.customOdds);
  const m = methodById(h.method);
  return h.charm ? m.charm : m.base;
}
const pct = (n) => (n < 0.1 ? n.toFixed(2) : n < 10 ? n.toFixed(1) : Math.round(n));

export async function ShinyView() {
  const view = el('section', { class: 'view lookup shinyview' });
  const snap = await loadSnapshot();
  const style = settings.get().spriteStyle;

  const list = el('div', { class: 'shiny' });

  function huntCard(h) {
    const sp = snap.speciesById.get(h.speciesId);
    const odds = oddsFor(h);

    const cumulative = (n) => 1 - Math.pow(1 - 1 / odds, n);
    const count = el('span', { class: 'shiny__count' }, String(h.count));
    const chanceEl = el(
      'span',
      { class: 'shiny__chance' },
      `${pct(cumulative(h.count) * 100)}% by now · 1/${odds}`,
    );
    const refresh = () => {
      const n = shinyHunts.get().hunts.find((x) => x.id === h.id)?.count ?? 0;
      count.textContent = String(n);
      chanceEl.textContent = `${pct(cumulative(n) * 100)}% by now · 1/${odds}`;
    };

    const notes = el('input', {
      type: 'text',
      class: 'shiny__notes',
      value: h.notes || '',
      placeholder: 'Notes — location, den, phase count…',
      onchange: (e) => updateHunt(h.id, { notes: e.target.value }),
    });

    return el(
      'div',
      { class: 'shiny__card' },
      el(
        'a',
        { class: 'shiny__mon', href: `#/pokemon/${h.speciesId}` },
        Sprite(h.speciesId, { style, shiny: true, alt: sp?.name, size: 56 }),
        el(
          'span',
          {},
          el('span', { class: 'shiny__name' }, (sp?.name || `#${h.speciesId}`).replace(/-/g, ' ')),
          el(
            'span',
            { class: 'shiny__method' },
            methodById(h.method).label + (h.charm ? ' · charm' : ''),
          ),
        ),
      ),
      el(
        'div',
        { class: 'shiny__counter' },
        el(
          'button',
          {
            type: 'button',
            class: 'shiny__step',
            onclick: () => {
              bumpHunt(h.id, -1);
              refresh();
            },
          },
          '−',
        ),
        count,
        el(
          'button',
          {
            type: 'button',
            class: 'shiny__step shiny__step--add',
            onclick: () => {
              bumpHunt(h.id, 1);
              buzz(10);
              refresh();
            },
          },
          '+1',
        ),
      ),
      chanceEl,
      notes,
      el(
        'div',
        { class: 'shiny__actions' },
        el(
          'button',
          {
            type: 'button',
            class: 'button',
            onclick: () => {
              if (!entry(h.speciesId).caught) toggle(h.speciesId, 'caught');
              removeHunt(h.id);
              render();
            },
          },
          '✨ Found it',
        ),
        el(
          'button',
          {
            type: 'button',
            class: 'button button--danger',
            onclick: async () => {
              const pick = await chooseDialog({
                title: 'Delete this hunt?',
                body: `${(sp?.name || 'This hunt').replace(/-/g, ' ')} — ${h.count} checks logged.`,
                choices: [
                  { key: 'del', label: 'Delete', class: 'button--danger' },
                  { key: null, label: 'Cancel', class: 'button--ghost' },
                ],
              });
              if (pick === 'del') {
                removeHunt(h.id);
                render();
              }
            },
          },
          'Delete',
        ),
      ),
    );
  }

  function newHunt() {
    openPokemonPicker(
      (id) => chooseMethod((method, charm) => addHunt({ speciesId: id, method, charm })),
      { closeAfterPick: true },
    );
  }

  function chooseMethod(done) {
    const backdrop = el('div', {
      class: 'sheet-backdrop is-open',
      onclick: (e) => e.target === backdrop && backdrop.remove(),
    });
    const methodSel = el(
      'select',
      { class: 'search__input' },
      ...METHODS.map((m) => el('option', { value: m.id }, m.label)),
    );
    const charmChk = el('input', { type: 'checkbox' });
    const panel = el(
      'div',
      { class: 'gsheet' },
      el(
        'div',
        { class: 'gsheet__head' },
        el('h2', {}, 'Hunt method'),
        el('button', { class: 'gsheet__close', onclick: () => backdrop.remove() }, '✕'),
      ),
      methodSel,
      el('label', { class: 'lookup__check', style: 'margin-top:12px' }, charmChk, 'Shiny Charm'),
      el(
        'button',
        {
          class: 'button',
          style: 'margin-top:16px',
          onclick: () => {
            done(methodSel.value, charmChk.checked);
            backdrop.remove();
            render();
          },
        },
        'Start hunt',
      ),
    );
    backdrop.append(panel);
    document.body.append(backdrop);
  }

  function render() {
    clear(list);
    const hunts = shinyHunts.get().hunts;
    if (!hunts.length) {
      list.append(el('p', { class: 'detail__muted' }, 'No active hunts. Start one below.'));
    } else {
      for (const h of hunts) list.append(huntCard(h));
    }
    list.append(
      el(
        'button',
        { type: 'button', class: 'lineup__add shiny__new', onclick: newHunt },
        '＋',
        el('span', {}, 'New hunt'),
      ),
    );
  }

  clear(view).append(
    el(
      'nav',
      { class: 'lookup__nav' },
      el('a', { class: 'link', href: '#/settings' }, '‹ Settings'),
    ),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, 'Shiny hunts'),
      el(
        'p',
        {},
        'Count encounters and watch the cumulative odds climb. “Found it” marks the Pokémon caught.',
      ),
    ),
    list,
  );
  render();

  onTeardown(view, () => {});
  return view;
}
